import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, AlertCircle, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white border border-[#E5E7EB] p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_humanitarian-aid-7/artifacts/5fygylhw_IMG_0281.jpeg"
                alt="Novena Foundation"
                className="w-16 h-16 mx-auto"
              />
            </Link>
            <h1 className="font-heading text-2xl font-bold text-[#0A0A0A]">
              Reset Your Password
            </h1>
            <p className="text-sm text-[#4B5563] mt-2">
              Enter your admin email and we'll send you a reset link.
            </p>
          </div>

          {submitted ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-4 rounded flex items-start gap-2" data-testid="forgot-password-success">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold mb-1">Check your email</p>
                  <p>If an account exists for <strong>{email}</strong>, a password reset link has been sent. It will expire in 30 minutes.</p>
                </div>
              </div>
              <p className="text-xs text-[#4B5563] text-center">
                Didn't receive it? Check your spam folder, or{' '}
                <button
                  type="button"
                  onClick={() => { setSubmitted(false); setEmail(''); }}
                  className="text-[#BA1826] font-medium hover:underline"
                  data-testid="forgot-password-try-again"
                >
                  try a different email
                </button>.
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 flex items-center gap-2" data-testid="forgot-password-error">
                  <AlertCircle className="w-5 h-5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#4B5563] mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] focus:ring-2 focus:ring-[#BA1826]/10"
                      placeholder="admin@novena.org"
                      required
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="forgot-password-submit-btn"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? 'Sending…' : 'Send Reset Link'}
                </button>
              </form>
            </>
          )}

          <div className="text-center mt-6">
            <Link to="/admin/login" className="text-sm text-[#4B5563] hover:text-[#BA1826] inline-flex items-center gap-1" data-testid="forgot-password-back-to-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
